import { isEmpty } from '@sgme/fp';
import classnames from 'classnames';
import { useUnderlyingContext } from 'hooks/useUnderlyingContext';
import { ChangeEvent, useCallback, useMemo } from 'react';

import { StyledCutOff } from './Data.styles';

interface Props {
  underlyingId: string;
  cutOff?: string;
  defaultCutOff?: string;
  cutOffs?: string[];
  showCutOff: boolean;
}

const emptyArray: readonly [] = [];

export function Data({ cutOff, cutOffs, defaultCutOff, showCutOff, underlyingId }: Props) {
  const { onUnderlyingCutOffChanged, isLoading, getCellHighlight } = useUnderlyingContext();

  const changeCutOff = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const { value } = event.currentTarget;

      if (isLoading || typeof underlyingId === 'undefined' || typeof onUnderlyingCutOffChanged !== 'function') {
        return;
      }

      onUnderlyingCutOffChanged(underlyingId, value);
    },
    [isLoading, onUnderlyingCutOffChanged, underlyingId],
  );

  const availableItems = useMemo<readonly string[]>(() => {
    if (isEmpty(cutOffs)) {
      return emptyArray;
    }
    return cutOffs!;
  }, [cutOffs]);

  const allItems = useMemo(() => {
    if (cutOff && availableItems.includes(cutOff)) {
      return availableItems;
    }

    return [cutOff, ...availableItems];
  }, [availableItems, cutOff]);

  if (!showCutOff) {
    return null;
  }

  if (isEmpty(availableItems) && !cutOff) {
    return <StyledCutOff className="border p-2"></StyledCutOff>;
  }

  if (isEmpty(availableItems) && cutOff) {
    return <StyledCutOff className="fs-6 p-3 ms-3">{cutOff}</StyledCutOff>;
  }

  return (
    <>
      <StyledCutOff className={classnames('border p-2 align-middle', getCellHighlight(underlyingId, 'CutOff'))}>
        <select
          className="pe-0 form-select fs-6 ps-2"
          data-e2e="underlying-cutoff"
          data-underlying-id={underlyingId}
          disabled={isLoading}
          onChange={changeCutOff}
          value={cutOff ?? defaultCutOff}
        >
          {allItems.map(value => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </StyledCutOff>
    </>
  );
}
